// src/store/apis/quotationApi.js
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

/* ------------------------------ helpers ------------------------------ */
const str = (x) => (x == null ? "" : String(x));
const key = (x) => String(x ?? "");
const sameId = (a, b) => key(a) === key(b);
const sameDoc = (it, id) => sameId(it?._id ?? it?.id, id);

const pickData = (res) => res?.data ?? res?.entry ?? res;

const toNum = (v) => Number(v ?? 0) || 0;
const round2 = (x) => Math.round((x + Number.EPSILON) * 100) / 100;
const round3 = (x) => Math.round((toNum(x) + Number.EPSILON) * 1000) / 1000;

const normLimit = (args, draft) =>
  Number(args?.limit ?? draft?.pagination?.limit ?? draft?.limit ?? 10) || 10;

/* ----------------------------- totals utils ----------------------------- */
const incTotals = (draft, by = 1) => {
  if (draft?.pagination?.totalDocs != null) {
    draft.pagination.totalDocs += by;
    const lim = draft?.pagination?.limit || draft?.limit || 10;
    draft.pagination.totalPages = Math.max(
      1,
      Math.ceil(draft.pagination.totalDocs / lim)
    );
  } else if (draft?.total != null) {
    draft.total += by;
    const lim = draft?.limit || draft?.pagination?.limit || 10;
    draft.pages = Math.max(1, Math.ceil(draft.total / lim));
  }
};

const decTotals = (draft, by = 1) => {
  if (draft?.pagination?.totalDocs != null) {
    draft.pagination.totalDocs = Math.max(0, draft.pagination.totalDocs - by);
    const lim = draft?.pagination?.limit || draft?.limit || 10;
    draft.pagination.totalPages = Math.max(
      1,
      Math.ceil(draft.pagination.totalDocs / lim)
    );
  } else if (draft?.total != null) {
    draft.total = Math.max(0, draft.total - by);
    const lim = draft?.limit || draft?.pagination?.limit || 10;
    draft.pages = Math.max(1, Math.ceil(draft.total / lim));
  }
};

/* --------------------------- list manipulation --------------------------- */
const addIntoListDraft = (draft, args, newItems = []) => {
  if (!Array.isArray(draft?.rows)) draft.rows = [];
  if (!newItems?.length) return;

  // agar current page 1 nahi hai to sirf totals badhao
  if (args.page && Number(args.page) !== 1) {
    incTotals(draft, newItems.length);
    return;
  }

  draft.rows.unshift(...newItems);

  const lim = normLimit(args, draft);
  if (lim > 0 && draft.rows.length > lim) {
    draft.rows.splice(lim);
  }
  incTotals(draft, newItems.length);
};

/* --------------------------- filter matching --------------------------- */
const matchesQtnFilters = (qtn = {}, args = {}) => {
  const {
    branchId = "",
    // customerId optional
    customerId = "",
    from = "",
    to = "",
    search = "", // client-side only
    status = "",
  } = args || {};

  // rowsOut already branchId/customerId flatten kar raha, but dono केस handle:
  const rowBranchId = qtn?.branchId?._id ?? qtn?.branchId;
  const rowCustomerId = qtn?.customerId?._id ?? qtn?.customerId;

  if (branchId && !sameId(rowBranchId, branchId)) return false;
  if (customerId && !sameId(rowCustomerId, customerId)) return false;

  if (status) {
    if (status === "pending") {
      if (!["Draft", "Final"].includes(qtn.status)) return false;
    } else if (qtn.status !== status) {
      return false;
    }
  }
  if (from || to) {
    const d = new Date(qtn.qtnDate || qtn.date || qtn.createdAt);
    if (from && d < new Date(from)) return false;
    if (to && d > new Date(to)) return false;
  }

  if (search) {
    const hay = [
      String(qtn?.qtnNo ?? ""),
      qtn?.qtnCode,
      qtn?.notes,
      qtn?.customerName,
      qtn?.customerCode,
      qtn?.branchName,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    if (!hay.includes(search.toLowerCase())) return false;
  }

  return true;
};

/* ----------------------- compute totals (client) ----------------------- */
// backend computeQuotationTotals jaisa, but sirf quotation fields
const computeQuotationTotals = (
  stockEntries = [],
  looseStones = [],
  looseDiamonds = [],
  opts = {}
) => {
  const discount = toNum(opts?.totalDiscount);
  const courier = toNum(opts?.courierCharge);
  const gstRoundOff = !!opts?.gstRoundOff;

  const t = {
    items: 0,
    qty: 0,
    totalWt: 0,
    packingWt: 0,
    grossWt: 0,
    stoneWt: 0,
    diamondWt: 0,
    netWt: 0,
    stonePcs: 0,
    diamondPcs: 0,

    subTotal: 0,
    gstTotal: 0,
    lineGrand: 0,

    discount: round2(discount),
    courier: round2(courier),
    roundOff: 0,
    grandTotal: 0,
  };

  for (const it of stockEntries || []) {
    t.items += 1;
    t.qty += toNum(it?.qty);
    t.totalWt += toNum(it?.totalWt);
    t.packingWt += toNum(it?.packingWt);
    t.grossWt += toNum(it?.grossWt);
    t.stoneWt += toNum(it?.stoneWt);
    t.diamondWt += toNum(it?.diamondWt);
    t.netWt += toNum(it?.netWt);
    t.stonePcs += toNum(it?.stonePcs);
    t.diamondPcs += toNum(it?.diamondPcs);
    t.subTotal += toNum(it?.itemAmount);
    t.gstTotal += toNum(it?.itemGstAmount);
    t.lineGrand += toNum(it?.totalItemAmount);
  }

  for (const s of looseStones || []) {
    t.items += 1;
    t.subTotal += toNum(s?.itemAmount);
    t.gstTotal += toNum(s?.itemGstAmount);
    t.lineGrand += toNum(s?.totalItemAmount);
  }

  for (const d of looseDiamonds || []) {
    t.items += 1;
    t.subTotal += toNum(d?.itemAmount);
    t.gstTotal += toNum(d?.itemGstAmount);
    t.lineGrand += toNum(d?.totalItemAmount);
  }

  const gross = t.lineGrand - t.discount + t.courier;
  const rounded = Math.round(gross);
  t.roundOff = gstRoundOff ? round2(rounded - gross) : 0;
  t.grandTotal = round2(gross + t.roundOff);

  t.totalWt = round3(t.totalWt);
  t.packingWt = round3(t.packingWt);
  t.grossWt = round3(t.grossWt);
  t.stoneWt = round3(t.stoneWt);
  t.diamondWt = round3(t.diamondWt);
  t.netWt = round3(t.netWt);

  return t;
};

/* ------------------- default args + cache seed/upsert ------------------- */
const defaultListArgs = (fromArgs = {}) => ({
  page: Number(fromArgs.page) || 1,
  limit: Number(fromArgs.limit) || 10,
  branchId: fromArgs.branchId || "",
  customerId: fromArgs.customerId || "",
  from: fromArgs.from || "",
  to: fromArgs.to || "",
  search: fromArgs.search || "",
  sortBy: fromArgs.sortBy,
  sortOrder: fromArgs.sortOrder,
});

const ensureListCacheAndInject = (dispatch, getState, args, rows) => {
  const a = defaultListArgs(args);
  const sel = quotationApi.endpoints.getQuotations.select(a)(getState());
  if (sel?.data) {
    dispatch(
      quotationApi.util.updateQueryData("getQuotations", a, (draft) => {
        addIntoListDraft(draft, a, rows);
      })
    );
  } else {
    const lim = normLimit(a);
    const draft = { rows: [], total: 0, page: a.page, limit: lim };
    addIntoListDraft(draft, a, rows);
    dispatch(quotationApi.util.upsertQueryData("getQuotations", a, draft));
  }
};

const upsertOrPatchRow = (dispatch, getState, args, updatedRow) => {
  const a = defaultListArgs(args);
  const sel = quotationApi.endpoints.getQuotations.select(a)(getState());
  if (sel?.data) {
    dispatch(
      quotationApi.util.updateQueryData("getQuotations", a, (draft) => {
        if (!Array.isArray(draft?.rows)) draft.rows = [];
        const idx = draft.rows.findIndex((it) => sameDoc(it, updatedRow?._id));
        if (idx >= 0) {
          draft.rows[idx] = { ...draft.rows[idx], ...updatedRow };
        } else if (matchesQtnFilters(updatedRow, a)) {
          addIntoListDraft(draft, a, [updatedRow]);
        }
      })
    );
  } else if (matchesQtnFilters(updatedRow, a)) {
    const lim = normLimit(a);
    dispatch(
      quotationApi.util.upsertQueryData("getQuotations", a, {
        rows: [updatedRow].slice(0, lim),
        total: 1,
        page: a.page,
        limit: lim,
      })
    );
  }
};

/* -------------------- build optimistic row from form -------------------- */
const buildTempQuotationRow = (payload, tempId) => {
  const nowIso = new Date().toISOString();

  const stockEntries = Array.isArray(payload?.stockEntries)
    ? payload.stockEntries.map((r) => ({
        qty: toNum(r?.qty),
        totalWt: toNum(r?.totalWt),
        packingWt: toNum(r?.packingWt),
        grossWt: toNum(r?.grossWt),
        stoneWt: toNum(r?.stoneWt),
        diamondWt: toNum(r?.diamondWt),
        netWt: toNum(r?.netWt),
        stonePcs: toNum(r?.stonePcs),
        diamondPcs: toNum(r?.diamondPcs),
        itemAmount: toNum(r?.itemAmount),
        itemGstAmount: toNum(r?.itemGstAmount),
        totalItemAmount: toNum(r?.totalItemAmount),
        // display fields
        itemCode: r?.itemCode?.label || "",
        categoryName: r?.category?.label || "",
        productName: r?.product?.label || "",
        designName: r?.design?.label || "",
        purityName: r?.purity?.label || "",
      }))
    : [];

  const looseStones = Array.isArray(payload?.looseStones)
    ? payload.looseStones.map((s) => ({
        itemAmount: toNum(s?.itemAmount),
        itemGstAmount: toNum(s?.itemGstAmount),
        totalItemAmount: toNum(s?.totalItemAmount),
        stoneName:
          s?.stnPopupName?.label || s?.stnPopupName?.info?.stoneName || "",
        unit: s?.unit || s?.stnPopupName?.info?.unit || "",
        wt: toNum(s?.stnPopupWt),
        pcs: toNum(s?.stnPopupPcs),
      }))
    : [];

  const looseDiamonds = Array.isArray(payload?.looseDiamonds)
    ? payload.looseDiamonds.map((d) => ({
        itemAmount: toNum(d?.itemAmount),
        itemGstAmount: toNum(d?.itemGstAmount),
        totalItemAmount: toNum(d?.totalItemAmount),
        diamondName: d?.diaName?.label || d?.diaName?.info?.diamondName || "",
        unit: "carat",
        ct: toNum(d?.diaWt),
        pcs: toNum(d?.pcs),
      }))
    : [];

  const totals = computeQuotationTotals(
    stockEntries,
    looseStones,
    looseDiamonds,
    {
      totalDiscount: payload?.totalDiscount,
      courierCharge: payload?.courierCharge,
      gstRoundOff: payload?.gstRoundOff,
    }
  );

  const branch = payload?.branch;
  const customer = payload?.customer;

  return {
    _id: tempId,
    qtnNo: payload?.qtnNo, // mostly undefined until backend returns
    qtnCode: payload?.qtnCode,
    qtnDate: payload?.qtnDate
      ? new Date(payload.qtnDate).toISOString()
      : nowIso,
    notes: payload?.notes || "",
    status: payload?.status || "Final",

    branchId: branch
      ? { _id: branch.value, branchName: branch.label }
      : payload?.branchId
      ? { _id: payload.branchId }
      : undefined,

    customerId: customer
      ? {
          _id: customer.value,
          customerName: customer?.info?.customerName || customer?.label || "",
          customerId: customer?.info?.customerId || customer?.label || "",
          contact: customer?.info?.contact,
        }
      : payload?.customerId
      ? { _id: payload.customerId }
      : undefined,

    stockEntries,
    looseStones,
    looseDiamonds,
    totals,

    isActive: true,
    __temp: true,
  };
};

/* --------------------------------------------------------------------- */

export const quotationApi = createApi({
  reducerPath: "quotationApi",
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_API_BASE_URL,
    credentials: "include",
  }),
  tagTypes: ["QTN", "QTN_LAST_NO"],
  endpoints: (builder) => ({
    /* ------------------------------ LIST ------------------------------ */
    getQuotations: builder.query({
      query: (args = {}) => {
        const {
          page = 1,
          limit = 10,
          branchId = "",
          customerId = "",
          from = "",
          to = "",
          search = "", // 🔍 global search
          qtnNo = "", // 🔢 quotation number only
          status = "",
          sortBy,
          sortOrder,
        } = args;

        const params = { page, limit };

        if (branchId) params.branchId = branchId;
        if (customerId) params.customerId = customerId;
        if (from) params.from = from;
        if (to) params.to = to;
        if (status) params.status = status;

        // ✅ SEARCH PARAMS
        if (search) params.q = search; // backend expects `q`
        if (qtnNo) params.qtnNo = qtnNo; // quotation number search

        if (sortBy) params.sortBy = sortBy;
        if (sortOrder) params.sortOrder = sortOrder;

        return {
          url: "/quotations",
          method: "GET",
          params,
        };
      },

      providesTags: ["QTN"],

      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const a = queryArgs || {};
        return [
          endpointName,
          a.page,
          a.limit,
          a.branchId,
          a.customerId,
          a.from,
          a.to,
          a.search,
          a.qtnNo,
          a.status,
          a.sortBy,
          a.sortOrder,
        ].join("|");
      },
    }),

    /* ----------------------------- GET ONE --------------------------- */
    getQuotationById: builder.query({
      query: (id) => `/quotations/${id}`,
      providesTags: (_res, _err, id) => [{ type: "QTN", id: key(id) }],
    }),

    /* ----------------------------- CREATE ---------------------------- */
    addQuotation: builder.mutation({
      query: (payload) => ({
        url: "/quotations",
        method: "POST",
        body: payload,
      }),
      async onQueryStarted(payload, { dispatch, queryFulfilled, getState }) {
        const tempId = `tmp_qtn_${Date.now()}`;
        const tempRow = buildTempQuotationRow(payload, tempId);

        const undoPatches = [];
        const allArgs =
          quotationApi.util.selectCachedArgsForQuery(
            getState(),
            "getQuotations"
          ) || [];

        // optimistic insert into all matching caches
        for (const args of allArgs) {
          if (!matchesQtnFilters(tempRow, args)) continue;
          undoPatches.push(
            dispatch(
              quotationApi.util.updateQueryData(
                "getQuotations",
                args,
                (draft) => {
                  addIntoListDraft(draft, args, [tempRow]);
                }
              )
            )
          );
        }

        // ensure at least default( page1 ) cache exists
        ensureListCacheAndInject(dispatch, getState, { page: 1, limit: 10 }, [
          tempRow,
        ]);

        try {
          const { data: res } = await queryFulfilled;
          const created = pickData(res);
          const real = Array.isArray(created) ? created[0] : created;
          if (!real) return;

          const again =
            quotationApi.util.selectCachedArgsForQuery(
              getState(),
              "getQuotations"
            ) || [];

          for (const args of again) {
            dispatch(
              quotationApi.util.updateQueryData(
                "getQuotations",
                args,
                (draft) => {
                  if (!Array.isArray(draft?.rows)) return;
                  const pos = draft.rows.findIndex((r) => sameDoc(r, tempId));
                  const nowMatches = matchesQtnFilters(real, args);

                  if (pos >= 0) {
                    if (nowMatches) {
                      draft.rows[pos] = { ...draft.rows[pos], ...real };
                      delete draft.rows[pos].__temp;
                    } else {
                      draft.rows.splice(pos, 1);
                      decTotals(draft, 1);
                    }
                  } else if (nowMatches) {
                    addIntoListDraft(draft, args, [real]);
                  }
                }
              )
            );
          }
        } catch {
          undoPatches.forEach((p) => p?.undo && p.undo());
        }
      },
      invalidatesTags: ["QTN_LAST_NO"],
    }),

    /* ----------------------------- UPDATE ---------------------------- */
    updateQuotation: builder.mutation({
      // call: updateQuotation({ id, ...patch })
      query: ({ id, ...patch }) => ({
        url: `/quotations/${id}`,
        method: "PUT",
        body: patch,
      }),
      async onQueryStarted(
        { id, ...patch },
        { dispatch, queryFulfilled, getState }
      ) {
        const patches = [];
        const allArgs =
          quotationApi.util.selectCachedArgsForQuery(
            getState(),
            "getQuotations"
          ) || [];

        // optimistic update in list
        for (const args of allArgs) {
          patches.push(
            dispatch(
              quotationApi.util.updateQueryData(
                "getQuotations",
                args,
                (draft) => {
                  if (!Array.isArray(draft?.rows)) return;
                  const idx = draft.rows.findIndex((it) => sameDoc(it, id));
                  if (idx >= 0) {
                    draft.rows[idx] = { ...draft.rows[idx], ...patch };
                  }
                }
              )
            )
          );
        }

        // optimistic update in getById cache
        patches.push(
          dispatch(
            quotationApi.util.updateQueryData(
              "getQuotationById",
              id,
              (draft) => {
                if (draft?.data) Object.assign(draft.data, patch);
              }
            )
          )
        );

        try {
          await queryFulfilled;
          // backend update sirf message return kar raha,
          // isliye yaha dubara server se row merge karne ki zarurat nahi.
        } catch {
          patches.forEach((p) => p?.undo && p.undo());
        }
      },
    }),

    /* ----------------------------- DELETE ---------------------------- */
    deleteQuotation: builder.mutation({
      query: (id) => ({
        url: `/quotations/${id}`,
        method: "DELETE",
      }),
      async onQueryStarted(id, { dispatch, queryFulfilled, getState }) {
        const patches = [];
        const allArgs =
          quotationApi.util.selectCachedArgsForQuery(
            getState(),
            "getQuotations"
          ) || [];

        for (const args of allArgs) {
          patches.push(
            dispatch(
              quotationApi.util.updateQueryData(
                "getQuotations",
                args,
                (draft) => {
                  if (!Array.isArray(draft?.rows)) return;
                  const before = draft.rows.length;
                  draft.rows = draft.rows.filter((it) => !sameDoc(it, id));
                  const removed = before - draft.rows.length;
                  if (removed > 0) decTotals(draft, removed);
                }
              )
            )
          );
        }

        try {
          await queryFulfilled;
        } catch {
          patches.forEach((p) => p?.undo && p.undo());
        }
      },
    }),

    /* ------------------------- LAST QTN NUMBER ------------------------ */
    getLastQuotationNumber: builder.query({
      // call: useGetLastQuotationNumberQuery({ branchId, date })
      query: (args = {}) => {
        const { branchId = "", date = "" } = args || {};
        const params = {};
        if (branchId) params.branchId = branchId;
        if (date) params.date = date;
        return {
          url: "/quotations/last-number",
          method: "GET",
          params,
        };
      },
      providesTags: ["QTN_LAST_NO"],
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const a = queryArgs || {};
        return [endpointName, a.branchId || "", a.date || ""].join("|");
      },
      keepUnusedDataFor: 30,
      refetchOnMountOrArgChange: true,
    }),

    /* ----------------------------- EXPIRE ---------------------------- */
    expireQuotation: builder.mutation({
      // call: expireQuotation(id)
      query: (id) => ({
        url: `/quotations/${id}/expire`,
        method: "PATCH",
      }),
      async onQueryStarted(id, { dispatch, queryFulfilled, getState }) {
        const patches = [];
        const allArgs =
          quotationApi.util.selectCachedArgsForQuery(
            getState(),
            "getQuotations"
          ) || [];

        // ✅ optimistic update in list caches
        for (const args of allArgs) {
          patches.push(
            dispatch(
              quotationApi.util.updateQueryData(
                "getQuotations",
                args,
                (draft) => {
                  if (!Array.isArray(draft?.rows)) return;
                  const idx = draft.rows.findIndex((it) => sameDoc(it, id));
                  if (idx >= 0) {
                    draft.rows[idx] = {
                      ...draft.rows[idx],
                      status: "Expired",
                      followUp: {
                        ...(draft.rows[idx]?.followUp || {}),
                        enabled: false,
                      },
                    };
                  }
                }
              )
            )
          );
        }

        // ✅ optimistic update in getById cache
        patches.push(
          dispatch(
            quotationApi.util.updateQueryData(
              "getQuotationById",
              id,
              (draft) => {
                if (draft?.data) {
                  draft.data.status = "Expired";
                  draft.data.followUp = {
                    ...(draft.data.followUp || {}),
                    enabled: false,
                  };
                }
              }
            )
          )
        );

        try {
          await queryFulfilled;
        } catch {
          patches.forEach((p) => p?.undo && p.undo());
        }
      },
    }),


    downloadQuotationPdf: builder.mutation({
  query: (id) => ({
    url: `/quotations/${id}/quotation.pdf`,
    method: "GET",
    responseHandler: (response) => response.text(),
  }),
}),
  }),
});

/* ----------------------------- hooks ----------------------------- */
export const {
  useGetQuotationsQuery,
  useGetQuotationByIdQuery,
  useAddQuotationMutation,
  useUpdateQuotationMutation,
  useDeleteQuotationMutation,
  useExpireQuotationMutation, // ✅ add this

  useGetLastQuotationNumberQuery,
  useDownloadQuotationPdfMutation,
} = quotationApi;

export default quotationApi;
